import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';

export interface VisitasPlanPick {
  idVisitaPlan: number;
  fecha: string;
  nroVendedor: number;
  nombreVendedor?: string | null;
}

interface VisitasPlanSearchDialogProps {
  open: boolean;
  loading?: boolean;
  plans: VisitasPlanPick[];
  onClose: () => void;
  onSelect: (plan: VisitasPlanPick) => void;
}

const formatDate = (value?: string | null): string => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('es-AR');
};

const formatVendedor = (nroVendedor: number, nombreVendedor?: string | null): string =>
  nombreVendedor ? `${nroVendedor} - ${nombreVendedor}` : String(nroVendedor);

const VisitasPlanSearchDialog: React.FC<VisitasPlanSearchDialogProps> = ({
  open,
  loading = false,
  plans,
  onClose,
  onSelect,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return plans;
    return plans.filter((p) => {
      const text = `#${p.idVisitaPlan} ${formatDate(p.fecha)} ${formatVendedor(p.nroVendedor, p.nombreVendedor)}`.toLowerCase();
      return text.includes(term);
    });
  }, [plans, searchTerm]);

  const handleClose = () => {
    setSearchTerm('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Seleccionar plan</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <TextField
          fullWidth
          placeholder="Buscar por N° de plan, fecha o vendedor"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        {loading ? (
          <Typography color="text.secondary">Cargando planes...</Typography>
        ) : filtered.length === 0 ? (
          <Typography color="text.secondary">No hay planes para seleccionar.</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Plan</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Vendedor</TableCell>
                  <TableCell align="center">Acción</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((p) => (
                  <TableRow key={p.idVisitaPlan} hover>
                    <TableCell>#{p.idVisitaPlan}</TableCell>
                    <TableCell>{formatDate(p.fecha)}</TableCell>
                    <TableCell>{formatVendedor(p.nroVendedor, p.nombreVendedor)}</TableCell>
                    <TableCell align="center">
                      <Button variant="outlined" size="small" onClick={() => onSelect(p)}>
                        Seleccionar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default VisitasPlanSearchDialog;
